import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@zoopla-eng/zds-components/button';
import { Box } from '@zoopla-eng/zds-components/box';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { InputPassword } from '@zoopla-eng/zds-components/input-field';

import { styledGeneralErrorStyle } from '../shared/shared.styles.css';
import { useEnforcedChangePassword } from '../../hooks/useEnforcedChangePassword';
import { WithTrackingData } from '../../AuthModal.types';
import { UserWithNextBestAction } from '../../hooks/authMutationHook.types';
import { usePasswordWithHints } from '@zoopla/authentication/helpers';

type ChangePasswordFormData = {
    'new-password': string;
    confirm_password: string;
};

type ChangePasswordFormProps = {
    email: string;
    oldPassword: string;
    onCompleted: (user: UserWithNextBestAction) => void;
} & WithTrackingData;

const verifyPasswordGuidance = 'Please enter the same value again';

export const ChangePasswordForm = ({
    email,
    oldPassword,
    onCompleted,
    trackingData
}: ChangePasswordFormProps) => {
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<ChangePasswordFormData>({
        mode: 'onBlur',
        reValidateMode: 'onBlur'
    });
    const { passwordHints, hintsSummary, validatePasswordHints } =
        usePasswordWithHints();

    const changePasswordAndAuthenticate = useEnforcedChangePassword({
        trackingData
    });

    const onSubmit = handleSubmit(async (formData) => {
        setIsLoading(true);

        try {
            const user = await changePasswordAndAuthenticate({
                email,
                password: oldPassword,
                newPassword: formData['new-password'],
                newPasswordConfirm: formData.confirm_password
            });

            onCompleted(user);
        } catch (e) {
            setIsError(true);
            setIsLoading(false);
        }
    });

    return (
        <form
            onSubmit={onSubmit}
            noValidate
            method="post"
            data-testid="auth-change-password-form"
        >
            <Box pb="S400">
                <InputPassword.WithHints
                    label="New password"
                    aria-required
                    hints={passwordHints}
                    accessibilityHintsSummary={hintsSummary}
                    {...register('new-password', {
                        validate: validatePasswordHints
                    })}
                />
            </Box>

            <InputPassword.Simple
                label="Verify new password"
                aria-required
                errorText={
                    errors.confirm_password &&
                    (errors.confirm_password.message || verifyPasswordGuidance)
                }
                {...register('confirm_password', {
                    required: true,
                    validate: (value, formValues) =>
                        value === formValues['new-password'] ||
                        verifyPasswordGuidance
                })}
            />

            <Box pt="S500" display="flex" flexWrap="wrap">
                <Box pb="S400">
                    <div role="alert">
                        {isError && (
                            <p
                                className={`${styledGeneralErrorStyle} ${fontGroups.fork}`}
                                data-testid="general-error"
                            >
                                Sorry, something went wrong. Please try again.
                            </p>
                        )}
                    </div>
                </Box>

                <Button
                    category="primary"
                    loading={isLoading && !isError}
                    loadingText="Submitting"
                    fw
                >
                    Submit
                </Button>
            </Box>
        </form>
    );
};
